import { inject, Injectable } from '@angular/core';
import { GlobalWebsocketService } from '@approot/global-websocket';
import { AlertsService } from '@approot/services';
import { clone, sleep } from '@approot/utilities';
import { isEqual } from 'lodash';
import { Observable, ReplaySubject, Subject, Subscription } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import {
  AllocateTradeState,
  Row,
  RunningState,
  WSRow
} from './allocate-trade.types';

export type CompletionStatus = 'success' | 'error' | undefined;

const DefaultAllocateTradeState: AllocateTradeState = {
  uuid: null,
  runningState: RunningState.Idle,
  message: null
};

@Injectable()
export class AllocateTradeService {
  public stateChanged: Observable<AllocateTradeState>;
  public complete$ = new Subject<CompletionStatus>();

  private state: AllocateTradeState = {};
  private state$ = new ReplaySubject<AllocateTradeState>(1);
  private eventSub: Subscription = null;
  private timer: any = null;
  private websocketService = inject(GlobalWebsocketService);
  private alertsService = inject(AlertsService);
  private runningState: 'INITIAL' | 'RUNNING' | 'COMPLETED' = 'INITIAL';

  constructor() {
    this.setState({ ...DefaultAllocateTradeState });
    this.stateChanged = this.state$.asObservable();
  }

  public async runAllocateTrade(payload: any) {
    const uuid = await this.websocketService.sendMessage({
      action: 'apiv2_createAndSaveTradesFromAllocationParameters',
      payload: {
        ...payload,
        task: 'createAndSaveTradesFromAllocationParameters'
      }
    });
    this.setState({ ...DefaultAllocateTradeState, uuid }, false);
    clearTimeout(this.timer);
    this.timer = setTimeout(() => {
      if (this.state.runningState === RunningState.Running) {
        this.reset('Allocate Trade timed out');
        this.alertsService.addAlert({
          type: 'error',
          message: 'Allocate Trade timed out'
        });
      }
    }, 5 * 60_000);
  }

  public reset(message: string = null) {
    this.unsubscribe();
    this.setState({ ...DefaultAllocateTradeState, message });
    clearTimeout(this.timer);
    this.timer = null;
  }

  /****<PRIVATE>**************************************************************/

  private setState(state: AllocateTradeState, emit = true) {
    const ouuid = this.state.uuid;
    const newState = { ...this.state, ...state };
    if (isEqual(newState, this.state)) {
      return;
    }

    this.state = clone(newState, true);
    if (emit) {
      this.state$.next(this.state);
    }
    if (state.uuid && state.uuid !== ouuid) {
      this.subscribeToAllocations(state.uuid);
    }
  }

  private unsubscribe(status: CompletionStatus = undefined) {
    if (status !== undefined) {
      this.complete$.next(status);
    }
    this.eventSub?.unsubscribe();
    this.eventSub = null;
    this.runningState = 'INITIAL';
  }

  private subscribeToAllocations(uuid: string) {
    this.runningState = 'RUNNING';
    this.unsubscribe();
    this.setState({
      uuid,
      runningState: RunningState.Running
    });

    const resubscribe = (iteration: number) => {
      if (iteration > 5) {
        this.setState({
          message: 'There was a problem getting the allocations'
        });
        return;
      }
      this.eventSub?.unsubscribe();
      this.eventSub = this.websocketService
        .getListener({ uuid, take: Infinity })
        .pipe(
          takeUntil(this.complete$),
          map((row: WSRow) => {
            return { ...row.payload, uuid: row.uuid } as Row;
          })
        )
        .subscribe({
          next: r => {
            this.setState({ message: r.message });
            if (r.error || r.warning) {
              this.handleError(r);
            } else if (r.info) {
              this.handleInfo(r);
            } else if (r.end) {
              this.handleEnd(r);
            } else {
              console.error('Unknown row type', r);
            }
          },
          error: async err => {
            console.error('Error getting allocations', err);
            await sleep(500);
            resubscribe(iteration + 1);
          },
          complete: async () => {
            if (this.runningState === 'COMPLETED') {
              return;
            }
            console.log('Resubscribing to allocations');
            await sleep(500);
            resubscribe(iteration + 1);
          }
        });
    };
    resubscribe(0);
  }

  private handleError(row: Row) {
    this.unsubscribe('error');
    this.setState({ ...DefaultAllocateTradeState, message: row.message });
    this.alertsService.addAlert({
      type: row.error ? 'error' : 'warning',
      message: row.message || 'There was a problem allocating the trade',
      config: {
        closeButton: true,
        tapToDismiss: true,
        timeOut: 3000
      }
    });
  }

  private handleInfo(row: Row) {
    this.setState({ message: row.message });
  }

  private handleEnd(row: Row) {
    this.runningState = 'COMPLETED';
    this.unsubscribe('success');
    this.reset();
  }
}
